import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UploadCloud, Check, ShieldCheck, FileText, Sparkles } from "lucide-react";

function Row({ label, v, highlight }) {
  return (
    <div className={`rounded-xl p-3 ${highlight ? "bg-emerald-500/10" : "bg-[var(--surface-alt)]"}`}>
      <div className="text-xs text-zinc-500 font-semibold">{label}</div>
      <div className={`font-display text-xl font-bold ${highlight ? "text-emerald-400" : "text-[var(--text)]"}`}>{v}</div>
    </div>
  );
}

export default function ImportPage() {
  const navigate = useNavigate();
  return (
    <AppShell>
      <div className="px-5 pt-4">
        <h1 className="font-display text-2xl font-bold text-[var(--text)]" data-testid="import-title">Import & enrichissement</h1>
        <p className="text-sm text-zinc-500">Deux modes : base de leads ou fichier interne client.</p>
      </div>

      <div className="px-5 mt-4">
        <Tabs defaultValue="leads">
          <TabsList className="grid grid-cols-2 rounded-full bg-slate-200 p-1 w-full">
            <TabsTrigger value="leads" className="rounded-full data-[state=active]:bg-emerald-500 data-[state=active]:text-white" data-testid="tab-prospects">Base prospects</TabsTrigger>
            <TabsTrigger value="client" className="rounded-full data-[state=active]:bg-emerald-500 data-[state=active]:text-white" data-testid="tab-client">Fichier client</TabsTrigger>
          </TabsList>

          <TabsContent value="leads" className="mt-4">
            <ProspectsImport />
          </TabsContent>
          <TabsContent value="client" className="mt-4">
            <ClientFileImport onDone={(pid) => pid && navigate(`/prospect/${pid}`)} />
          </TabsContent>
        </Tabs>
      </div>

      <div className="px-5 mt-5 bg-emerald-500/10 rounded-2xl p-4 border border-emerald-100 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-emerald-300 shrink-0 mt-0.5" />
        <div className="text-xs text-zinc-300 leading-relaxed">
          <strong className="text-emerald-300">Données confidentielles.</strong> Les fichiers importés sont traités par notre IA pour en extraire la valeur, puis stockés chiffrés sur nos serveurs européens. <strong>Aucune donnée n'est réutilisée</strong> pour entraîner d'autres modèles.
        </div>
      </div>
    </AppShell>
  );
}

function ProspectsImport() {
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handle = async () => {
    if (!file) return;
    setUploading(true); setProgress(10);
    const int = setInterval(() => setProgress((p) => Math.min(p + 15, 90)), 300);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("prosperr_token");
      const r = await fetch(`${API_BASE}/import`, {
        method: "POST", body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setResult(data); setProgress(100);
      toast.success(`${data.new} leads importés`);
    } catch { toast.error("Erreur import"); }
    finally { clearInterval(int); setUploading(false); }
  };

  return (
    <div>
      <label className="block bg-[var(--surface)] rounded-xl p-8 border-2 border-dashed border-[var(--border)] text-center cursor-pointer hover:border-emerald-300" data-testid="file-drop">
        <UploadCloud className="w-10 h-10 mx-auto text-zinc-500" />
        <div className="mt-3 font-semibold text-zinc-200">{file ? file.name : "Glissez votre fichier ou cliquez"}</div>
        <div className="text-xs text-zinc-500 mt-1">.xlsx, .xls, .csv, .tsv, .vcf — Max 10 MB</div>
        <input type="file" accept=".xlsx,.xls,.csv,.tsv,.vcf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="file-input" />
      </label>

      {file && !result && (
        <div className="flex gap-3 mt-4">
          <Button 
            onClick={handle} 
            disabled={uploading} 
            className="flex-1 rounded-full bg-emerald-500/100 hover:bg-emerald-500 h-11 font-semibold" 
            data-testid="import-btn"
          >
            {uploading ? "Prosperr analyse les leads…" : "Importer et scorer"}
          </Button>
          <Button 
            onClick={() => setFile(null)}
            variant="outline"
            className="rounded-full h-11 px-6 font-semibold"
          >
            Annuler
          </Button>
        </div>
      )}
      {uploading && (
        <div className="mt-4">
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-emerald-500/100 transition-all" style={{ width: `${progress}%` }} /></div>
          <div className="text-xs text-zinc-500 mt-2 text-center">Détection doublons → nettoyage → scoring Prosperr → recommandation canal</div>
        </div>
      )}
      {result && (
        <div className="mt-5 space-y-3" data-testid="import-results">
          <div className="bg-[var(--surface)] rounded-xl p-5 border border-[var(--border-soft)] shadow-sm">
            <div className="font-display font-bold text-[var(--text)] flex items-center gap-2"><Check className="w-5 h-5 text-emerald-500" />Import terminé</div>
            <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
              <Row label="Total lignes" v={result.total} />
              <Row label="Doublons" v={result.duplicates} />
              <Row label="Déjà dans CRM" v={result.already} />
              <Row label="À contacter" v={result.new} highlight />
              <Row label="Score moyen" v={`${result.avg_score}/100`} />
              <Row label="Leads chauds (70+)" v={result.hot_leads} highlight />
            </div>
          </div>
          <Button onClick={() => { window.location.href = "/pipeline"; }} className="w-full rounded-full bg-emerald-500/100 hover:bg-emerald-500 h-11 font-semibold" data-testid="go-pipeline">Voir dans le pipeline</Button>
        </div>
      )}
    </div>
  );
}

function ClientFileImport({ onDone }) {
  const [file, setFile] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [prospectId, setProspectId] = useState("");
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    api.get("/prospects").then((r) => setProspects(r.data)).catch(() => {});
  }, []);

  const run = async () => {
    if (!file) return;
    setLoading(true); setProgress(10);
    const int = setInterval(() => setProgress((p) => Math.min(p + 10, 85)), 500);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (prospectId) fd.append("prospect_id", prospectId);
      const token = localStorage.getItem("prosperr_token");
      const r = await fetch(`${API_BASE}/import/client-file`, {
        method: "POST", body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Erreur");
      }
      const data = await r.json();
      setInsights(data);
      setProgress(100);
      toast.success(`${data.actions_created} actions de relance créées`);
    } catch (e) { toast.error(e.message || "Erreur"); }
    finally { clearInterval(int); setLoading(false); }
  };

  return (
    <div className="space-y-3">
      <div className="bg-[var(--surface)] rounded-xl p-4 border border-[var(--border-soft)] shadow-sm">
        <div className="font-display font-bold text-[var(--text)] flex items-center gap-2"><Sparkles className="w-5 h-5 text-emerald-500" />Enrichissement client par Prosperr</div>
        <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
          Importez un fichier interne d'un client (historique commandes, organigramme, notes internes, CR de réunion en CSV/Excel). L'IA extrait contacts, deals passés, points de douleur, opportunités — et crée automatiquement les actions de relance pertinentes.
        </p>
      </div>

      <div>
        <div className="text-xs font-semibold text-zinc-400 mb-1.5">Rattacher à un lead existant (recommandé)</div>
        <Select value={prospectId} onValueChange={setProspectId}>
          <SelectTrigger className="rounded-xl" data-testid="client-prospect-select"><SelectValue placeholder="Optionnel — sélectionner un lead" /></SelectTrigger>
          <SelectContent className="max-h-[300px]">
            {prospects.map((p) => <SelectItem key={p.prospect_id} value={p.prospect_id}>{p.name} · {p.company}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <label className="block bg-[var(--surface)] rounded-xl p-8 border-2 border-dashed border-[var(--border)] text-center cursor-pointer hover:border-emerald-300" data-testid="client-file-drop">
        <FileText className="w-10 h-10 mx-auto text-zinc-500" />
        <div className="mt-3 font-semibold text-zinc-200">{file ? file.name : "Glissez le fichier client"}</div>
        <div className="text-xs text-zinc-500 mt-1">.xlsx, .xls, .csv, .tsv, .txt — Max 10 MB</div>
        <input type="file" accept=".xlsx,.xls,.csv,.tsv,.txt" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="client-file-input" />
      </label>

      {file && !insights && (
        <Button onClick={run} disabled={loading} className="w-full rounded-full bg-emerald-500/100 hover:bg-emerald-500 h-11 font-semibold" data-testid="client-import-btn">
          {loading ? "Prosperr analyse…" : "Analyser et créer les relances"}
        </Button>
      )}
      {loading && (
        <div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-emerald-500/100 transition-all" style={{ width: `${progress}%` }} /></div>
          <div className="text-xs text-zinc-500 mt-2 text-center">Extraction contacts → deals passés → opportunités → création des actions</div>
        </div>
      )}

      {insights && (
        <div className="space-y-3" data-testid="client-results">
          <div className="bg-[var(--surface)] rounded-xl p-5 border border-[var(--border-soft)] shadow-sm">
            <div className="font-display font-bold text-[var(--text)]">Synthèse</div>
            <p className="text-sm text-zinc-300 mt-2 leading-relaxed">{insights.insights?.summary}</p>
          </div>
          {insights.insights?.opportunities?.length > 0 && (
            <div className="bg-emerald-500/10 rounded-2xl p-5 border border-emerald-100">
              <div className="font-display font-bold text-emerald-300">Opportunités détectées</div>
              <ul className="mt-2 space-y-1 text-sm text-zinc-300 list-disc ml-4">
                {insights.insights.opportunities.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            </div>
          )}
          {insights.insights?.pain_points?.length > 0 && (
            <div className="bg-amber-50 rounded-2xl p-5 border border-amber-100">
              <div className="font-display font-bold text-amber-700">Points de douleur</div>
              <ul className="mt-2 space-y-1 text-sm text-zinc-300 list-disc ml-4">
                {insights.insights.pain_points.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
          )}
          <div className="bg-emerald-500/10 rounded-2xl p-5 border border-emerald-500/30">
            <div className="font-display font-bold text-emerald-300">{insights.actions_created} actions programmées</div>
            <p className="text-xs text-zinc-400 mt-1">Retrouvez-les dans la fiche lead et le tunnel.</p>
          </div>
          {prospectId && <Button onClick={() => onDone(prospectId)} className="w-full rounded-full bg-emerald-500/100 hover:bg-emerald-500 h-11 font-semibold" data-testid="go-prospect">Voir la fiche lead</Button>}
        </div>
      )}
    </div>
  );
}
